import { ReactElement, useState, useEffect, useCallback,useRef } from "react";
import { Paper, Typography, Button, Chip, IconButton } from "@mui/material";
import {
  CloudUpload as CloudUploadIcon,
  Download as DownloadIcon,
  Close as CloseIcon,
  AutoAwesome as AutoAwesomeIcon,
} from "@mui/icons-material";
import { useProjectStore } from "../stores/project.store";
import { useAuth } from "../contexts/auth.context";
import projectsService from "../services/projects.service";
import ProjectEditor from "../components/ProjectEditor.component";
import FileUploadForm from "../components/forms/FileUploadForm";
import ProjectForm from "../components/forms/ProjectForm";
import ConfirmationForm from "../components/forms/ConfirmationForm";
import WorkflowInfo from "../components/WorkflowInfo.component";
import WorkflowPanel from "../components/WorkflowPanel.component";
import StepDetailsPanel from "../components/StepDetailsPanel.component";
import ResizablePanel from "../components/ResizablePanel.component";
import BaseModal from "../components/modals/BaseModal";
import ResetWarningModal from "../components/modals/ResetWarningModal";
import PostClusteringModal from "../components/modals/PostClusteringModal";
import { getWorkflowConfiguration } from "../config/workflow.config";
import PrecomputedDemosPanel from "../components/PrecomputedDemosPanel";
import type { AnalysisRun } from "../services/projects.service";

const PLOT_SAMPLE_SIZE = 2000;

const visibleStepParameters = (params: Record<string, any> | null | undefined) =>
  Object.fromEntries(
    Object.entries(params || {}).filter(
      ([key, value]) =>
        !key.startsWith("_") &&
        key !== "fileContent" &&
        value !== undefined,
    ),
  );



export interface ProjectPageProps {
  className?: string;
  onInfo?: () => void;
  onRefresh?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
}

const ProjectPage = ({
  className = "",
  onInfo,
  onRefresh,
  onEdit,
  onDelete,
}: ProjectPageProps): ReactElement => {
  const {
    selectedProject,
    deleteProject,
    fileCount,
    uploadedFileNames,
    selectedFiles,
    projectFiles,
    hasStartedAnalysis,
    activeStep,
    isProcessing,
    stepResults,
    completedSteps,
    analysisError,
    setFileCount,
    setUploadedFileNames,
    setSelectedFiles,
    setProjectFiles,
    setHasStartedAnalysis,
    setActiveStep,
    setIsProcessing,
    saveStepResult,
    deleteStepResult,
    loadStepResults,
    stepResultKeys,
    setCompletedSteps,
    setAnalysisError,
    setBackendResult,
    setAnalysisData,
    resetProjectAnalysis,
    activeRunId,
    setActiveRunId,
  } = useProjectStore();
  const { auth } = useAuth();

  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showBackWarningModal, setShowBackWarningModal] = useState(false);
  const [targetStepForBack, setTargetStepForBack] = useState<string | null>(
    null,
  );
  const [showResetWarningModal, setShowResetWarningModal] = useState(false);
  const [showPostClusteringModal, setShowPostClusteringModal] = useState(false);
  const [showDemosModal, setShowDemosModal] = useState(false);

  const handleResumeRun = async (run: AnalysisRun, runProjectName: string) => {
    const { projects, selectProject, setActiveTab } = useProjectStore.getState();
    if (selectedProject?.project_name !== runProjectName) {
      const runProject = projects.find((project) => project.project_name === runProjectName);
      if (!runProject) {
        setAnalysisError(`Could not find project ${runProjectName}. Refresh the project list and try again.`);
        return;
      }
      setSelectedFiles([]);
      setCompletedSteps([]);
      setActiveStep(null);
      setBackendResult(null);
      setHasStartedAnalysis(false);
      selectProject(runProject);
      setActiveTab(runProject.project_name);
    }

    const restoredSteps: string[] = [];
    for (const step of run.steps) {
      if (step.status !== "completed" || !step.summary) continue;
      const entry = Object.entries(currentStepConfig.stepNameMapping).find(
        ([, backendName]) => backendName === step.step_name,
      );
      if (!entry) continue;
      await saveStepResult(entry[0], {
        ...step.summary,
        parameters: visibleStepParameters(step.parameters),
      });
      restoredSteps.push(entry[0]);
    }
    setCompletedSteps(restoredSteps);
    // A precomputed snapshot is immutable. Starting a normal step afterwards
    // creates a regular run instead of modifying the saved demo run.
    setActiveRunId(null);
    const lastStep = restoredSteps[restoredSteps.length - 1];
    if (lastStep) {
      setActiveStep(lastStep);
      setBackendResult(stepResults[lastStep] || run.steps.find(
        (step) => currentStepConfig.stepNameMapping[lastStep] === step.step_name,
      )?.summary);
    }
    setShowDemosModal(false);
  };

  // Fetch files function
  const fetchProjectFiles = useCallback(async () => {
    if (!selectedProject || !auth?.accessToken) return;
    const requestedProject = selectedProject;

    try {
      const files = await projectsService.fetchProjectFiles(
        auth,
        requestedProject.project_name,
      );
      if (useProjectStore.getState().selectedProject?.id !== requestedProject.id) return;
      const fileNames = files.map((file) => file.name);
      setProjectFiles(files);
      setUploadedFileNames(fileNames);
      setFileCount(fileNames.length);
    } catch (error) {
      if (useProjectStore.getState().selectedProject?.id !== requestedProject.id) return;
      console.error("Failed to fetch project files:", error);
      // Reset to empty state on error
      setProjectFiles([]);
      setUploadedFileNames([]);
      setFileCount(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject, auth?.accessToken]);

  // Load step results from IndexedDB when component mounts
  useEffect(() => {
    if (stepResultKeys.length > 0) {
      loadStepResults(stepResultKeys);
    }
    // TODO: potentially causing unnecessary IndexedDB reads (track which keys already are loaded)
  }, [stepResultKeys, loadStepResults]);

  // Fetch files when project loads
  useEffect(() => {
    if (selectedProject) {
      fetchProjectFiles();
      // Note: Don't reset analysis state here as it would clear results on page refresh
      // Analysis state is only reset when explicitly switching projects in App.tsx
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject, auth?.accessToken, fetchProjectFiles]);

  // Update analysis data when selectedFiles changes (separate effect to avoid infinite loop)
  useEffect(() => {
    if (selectedProject) {
      setAnalysisData({
        projectName: selectedProject.project_name,
        dataType: selectedProject.data_type,
        selectedFiles: selectedFiles,
      });
    }
  }, [selectedProject, selectedFiles, setAnalysisData]);

  const handleViewFiles = () => {
    setShowUploadModal(true);
  };

  const handleFilesUploaded = (uploadedFiles: string[]) => {
    // Refresh the file list from server to get accurate data
    fetchProjectFiles();
    // Reset analysis state when new files are uploaded
    setHasStartedAnalysis(false);
    // Don't auto-select files - let user choose which ones to run
  };

  const handleFileUploadSubmit = (uploadedFiles: string[]) => {
    // Don't close modal - let user see uploaded files and select them
    handleFilesUploaded(uploadedFiles);
  };

  const handleFileSelect = (fileName: string, isSelected: boolean) => {
    if (isSelected) {
      setSelectedFiles((prev) => [...prev, fileName]);
    } else {
      setSelectedFiles((prev) => prev.filter((file) => file !== fileName));
    }
  };

  const handleSelectAllFiles = (isSelected: boolean) => {
    if (isSelected) {
      // Select all uploaded files
      setSelectedFiles([...uploadedFileNames]);
    } else {
      setSelectedFiles([]);
    }
  };

  const handleRunSelectedFiles = () => {
    // Start analysis with selected files
    setHasStartedAnalysis(true);
    // console.log('Running selected files:', selectedFiles);
  };

  const handleRunSelectedFilesFromModal = (fileNames: string[]) => {
    // Set the selected files from modal without adding duplicates
    setSelectedFiles(fileNames);
    // console.log('Running selected files from modal:', fileNames);
  };

  const handleFilesChanged = () => {
    // Refresh file list when files are modified (deleted, renamed, etc.)
    fetchProjectFiles();
  };

  const handleInfo = () => {
    setShowInfoModal(true);
  };

  const handleEdit = () => {
    setShowEditModal(true);
  };

  const handleEditSubmit = (updatedProject: any) => {
    setShowEditModal(false);
    // The project store will automatically update the selectedProject
  };

  const handleDelete = () => {
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedProject || !auth?.accessToken) return;

    try {
      setIsDeleting(true);
      const success = await deleteProject(auth, selectedProject.id);
      if (success) {
        setShowDeleteModal(false);
        // Project deleted successfully, selectedProject will be set to null by the store
      }
    } catch (error) {
      console.error("Failed to delete project:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
  };

  const handleStepSelect = (stepId: string) => {
    setAnalysisError("");
    setActiveStep(stepId);
  };

  // Get current data type (default to single-cell for now)
  const currentDataType = "single-cell";
  const currentStepConfig = getWorkflowConfiguration(currentDataType);



  type ResampleArgs = {
  step: string;        // "qc" | "umap" | "normalization" OR "Quality Control" etc.
  seed: number;
  artifactId: string;
  selectedGene?: string;
};

const handleResampleQc = useCallback(async (args: ResampleArgs) => {
  console.log("handleResampleQc triggered with arguments:", args);
  const store = useProjectStore.getState();

  const projectName = store.analysisData?.projectName;
  if (!projectName) {
    store.setAnalysisError("Missing projectName");
    return;
  }

  if (!auth?.accessToken) {
    store.setAnalysisError("Not authenticated. Login again.");
    return;
  }

  try {
    // 🚫 IMPORTANT: Do NOT use setIsProcessing for resample; it replaces your plot with WaitingScreen
    // store.setIsProcessing(true);

    const br = store.backendResult;
    const currentStep = br?.currentStep ?? "Quality Control";

    // Use the args step if provided, else fallback to currentStep
    const stepFromUi = (args.step || currentStep).trim();

    // Normalize step for backend
    const stepSlug =
      stepFromUi.toLowerCase() === "quality control"
        ? "qc"
        : projectsService.stepSlug(stepFromUi);

    // ✅ Use artifactId passed from UI (UMAP needs this)
    const artifactId =
      args.artifactId ||
      br?.artifactId ||
      br?.qcArtifactId ||
      br?.qcMeta?.qcArtifactId;

    if (!artifactId) {
      store.setAnalysisError("Missing artifactId. Run this step once first.");
      return;
    }
    console.log("RESAMPLE CALL", { stepSlug, artifactId, seed: args.seed });

    const res = await projectsService.qcResample(
      { accessToken: auth.accessToken },
      projectName,
      {
        step: stepSlug,
        artifactId,
        seed: args.seed,
        sample_n: PLOT_SAMPLE_SIZE,
        selectedGene: args.selectedGene ?? br?.selectedGene,
      }
    );

    // ✅ Update plot sample
    const prev = store.backendResult || {};
    const patchPlotSample = Object.fromEntries(
      Object.entries(res.plotSample || {}).map(([k, arr]) => [
        k,
        Array.isArray(arr) ? [...arr] : arr,
      ])
    );

    // ✅ Update meta for the correct step (QC used to always update qcMeta)
    const metaPatch = {
      sampleSeed: res.sampleSeed,
      sampleN: res.sampleN,
      totalCells: res.totalCells,
      artifactId: res.artifactId,
    };

    let stepMetaUpdate: any = {};
    if (stepSlug === "qc") stepMetaUpdate = { qcMeta: { ...(prev.qcMeta || {}), ...metaPatch } };
    else if (stepSlug === "normalization") stepMetaUpdate = { normalizationMeta: { ...(prev.normalizationMeta || {}), ...metaPatch } };
    else if (stepSlug === "umap") stepMetaUpdate = { umapMeta: { ...(prev.umapMeta || {}), ...metaPatch } };

    store.setBackendResult({
      ...prev,
      plotSample: patchPlotSample,
      ...stepMetaUpdate,
    });
  } catch (err: any) {
    store.setAnalysisError(err?.message ?? String(err));
  } finally {
    // store.setIsProcessing(false);
  }
}, [auth?.accessToken]);


const didRehydrateRef = useRef(false);

useEffect(() => {
  if (didRehydrateRef.current) return;

  const store = useProjectStore.getState();

  const isQcScreen = store.backendResult?.currentStep === "Quality Control";
  const isSingleCell = store.analysisData?.dataType?.toLowerCase?.() === "single cell data";

  if (!isQcScreen || !isSingleCell) return;

  // must have auth + artifact id
  if (!auth?.accessToken) return;
  if (!store.analysisData?.projectName) return;
  if (!store.backendResult?.qcArtifactId) return;

  // if we already have something to show, don't rehydrate
  const hasPlot =
    store.backendResult?.plotSample &&
    Object.keys(store.backendResult.plotSample).length > 0;

  const hasRows = (store.backendResult?.tableData?.length ?? 0) > 0;

  if (hasPlot || hasRows) return;

  didRehydrateRef.current = true;

  const seed = store.backendResult?.qcMeta?.sampleSeed ?? 0;

  handleResampleQc({
  step: "qc",
  seed,
  artifactId: store.backendResult.qcArtifactId,
});

}, [auth?.accessToken,handleResampleQc]);



  


  const handleStepSubmit = async (data: any) => {
    if (!auth?.accessToken || !selectedProject) {
      setAnalysisError("Authentication required");
      return;
    }

    const submittedProjectId = selectedProject.id;
    const submittedProjectName = selectedProject.project_name;
    const submittedStep = activeStep;
    const isSubmittedProjectActive = () =>
      useProjectStore.getState().selectedProject?.id === submittedProjectId;

    const selectedFileObjects = selectedFiles
      .map((fileName) => {
        const fileObj = projectFiles.find((file) => file.name === fileName);
        return {
          id: fileObj ? parseInt(fileObj.id, 10) : 0,
          name: fileName,
        };
      })
      .filter((file) => file.id !== 0);

    if (selectedFileObjects.length === 0 && activeStep !== "trajectory-analysis") {
      setAnalysisError(
        "Please select at least one input file from the Analysis panel before running this step.",
      );
      return;
    }

    setIsProcessing(true);
    setAnalysisError("");

    try {

      const enrichmentParams =
        activeStep === "enrichment"
          ? {
              ...data,
              // JSON cannot carry a browser File object. Send its text because
              // the enrichment worker consumes newline-separated identifiers.
              fileContent: data.file ? await data.file.text() : undefined,
              file: undefined,
            }
          : null;

      const payload = {
        selectedFiles: selectedFileObjects,
        selectedOptions: {
          mainCategory: "Preprocessing Type",
          subCategory: "Preprocessing",
          option: activeStep
            ? currentStepConfig.stepNameMapping[activeStep] || activeStep
            : "",
        },
        metadataEnabled: false,
        params:
          activeStep === "enrichment"
            ? enrichmentParams
            : activeStep === "marker-gene-set"
              ? data
              : {
                  ...data,
                  filterValue: data.filterValue
                    ? String(data.filterValue)
                    : data.filterValue,
                },
        currentStep: activeStep
          ? currentStepConfig.stepNameMapping[activeStep] || activeStep
          : "",
        nextStep: null,
        runId:
          currentStepConfig.stepNameMapping[activeStep || ""] === "Quality Control"
            ? undefined
            : activeRunId || undefined,
      };
      const submittedParameters = visibleStepParameters(payload.params);
      useProjectStore.getState().setLastQcPayload(payload);

      // Call the API
      const result = await projectsService.runWorkflowStep(
        auth,
        submittedProjectName,
        payload,
      );
      if (!isSubmittedProjectActive()) return;
      if ((result as any).run_id) {
        setActiveRunId((result as any).run_id);
      }

      if (result.task_id) {
        await projectsService.pollTaskStatus(
          result.task_id,
          auth,
          async (result) => {
            if (!isSubmittedProjectActive()) return;
            const shouldMarkCompleted =
              submittedStep &&
              result &&
              ((result as any).tableData ||
                (result as any).status === "success");

            setIsProcessing(false);
            const resultWithParameters = {
              ...result,
              parameters: submittedParameters,
            };
            if (submittedStep) {
              await saveStepResult(submittedStep, resultWithParameters);
            }
            // setBackendResult(result);
            useProjectStore.getState().setBackendResult({
              ...resultWithParameters,
            });


            if (shouldMarkCompleted) {
              setCompletedSteps((prev) => {
                if (submittedStep && !prev.includes(submittedStep)) {
                  return [...prev, submittedStep];
                }
                return prev;
              });
            }
          },
          (error) => {
            if (!isSubmittedProjectActive()) return;
            console.error("Workflow step failed:", error);
            setAnalysisError(error);
            setIsProcessing(false);
          },
        );
      } else {
        if (!isSubmittedProjectActive()) return;
        setIsProcessing(false);
        const resultWithParameters = {
          ...result,
          parameters: submittedParameters,
        };
        if (submittedStep) {
          await saveStepResult(submittedStep, resultWithParameters);
        }
        if (
          submittedStep &&
          result &&
          ((result as any).tableData || (result as any).status === "success")
        ) {
          setCompletedSteps((prev) => {
            if (submittedStep && !prev.includes(submittedStep)) {
              return [...prev, submittedStep];
            }
            return prev;
          });
        }
        // setBackendResult(result);
          useProjectStore.getState().setBackendResult({
              ...resultWithParameters,
            });
        // setActiveStep(null);
      }
    } catch (err) {
      if (!isSubmittedProjectActive()) return;
      console.error("Error running workflow step:", err);
      const errorMessage =
        err instanceof Error ? err.message : "Failed to run workflow step";
      setAnalysisError(errorMessage);
      setIsProcessing(false);

      if (errorMessage.includes("500")) {
        setAnalysisError(
          "Server error occurred. Please check the server logs or try again later.",
        );
      }
    }
  };

  const handleStepClose = () => {
    setActiveStep(null);
  };

  const handleCancelProcessing = () => {
    setIsProcessing(false);
    setActiveStep(null);
  };

  const handleContinue = () => {
    if (!activeStep) return;

    if (activeStep === "clustering") {
      setShowPostClusteringModal(true);
      return;
    }

    const currentStepIndex = currentStepConfig.stepOrder.indexOf(activeStep);

    if (
      currentStepIndex !== -1 &&
      currentStepIndex < currentStepConfig.stepOrder.length - 1
    ) {
      const nextStep = currentStepConfig.stepOrder[currentStepIndex + 1];
      setActiveStep(nextStep);
    }
  };

  const handleBack = () => {
    if (!activeStep) return;

    const currentStepIndex = currentStepConfig.stepOrder.indexOf(activeStep);

    const stepsToReset = currentStepConfig.stepOrder
      .slice(currentStepIndex + 1)
      .filter((step) => completedSteps.includes(step));

    if (stepsToReset.length > 0) {
      setTargetStepForBack(activeStep);
      setShowBackWarningModal(true);
    } else {
      handleResetStep();
    }
  };

  const handleStepBack = () => {
    if (!activeStep) return;

    if (activeStep === "enrichment") {
      setActiveStep("de-genes-markers");
      return;
    }

    const postClusteringSteps = [
      "cell-type-annotation",
      "marker-gene-set",
      "de-genes-markers",
      "trajectory-analysis",
    ];

    if (postClusteringSteps.includes(activeStep)) {
      setActiveStep("clustering");
      return;
    }

    const currentStepIndex = currentStepConfig.stepOrder.indexOf(activeStep);

    if (currentStepIndex > 0) {
      const previousStep = currentStepConfig.stepOrder[currentStepIndex - 1];
      setActiveStep(previousStep);
    }
  };

  const performBackNavigation = async (targetStep: string) => {
    const targetStepIndex = currentStepConfig.stepOrder.indexOf(targetStep);

    const stepsToReset = currentStepConfig.stepOrder.slice(targetStepIndex + 1);

    // Delete from IndexedDB and update local state
    const stepsToDelete = [...stepsToReset];
    if (targetStep === activeStep) {
      stepsToDelete.push(targetStep);
    }

    for (const step of stepsToDelete) {
      await deleteStepResult(step);
    }

    setCompletedSteps((prev) =>
      prev.filter((step) => !stepsToReset.includes(step)),
    );

    if (targetStep === activeStep) {
      setCompletedSteps((prev) => prev.filter((step) => step !== targetStep));
    }

    if (targetStep !== activeStep) {
      setActiveStep(targetStep);
    }
  };

  const handleBackWarningConfirm = async () => {
    if (targetStepForBack) {
      await performBackNavigation(targetStepForBack);
    }
    setShowBackWarningModal(false);
    setTargetStepForBack(null);
  };

  const handleBackWarningCancel = () => {
    setShowBackWarningModal(false);
    setTargetStepForBack(null);
  };

  const handleReset = () => {
    if (completedSteps.length > 0) {
      setShowResetWarningModal(true);
    } else {
      performFullReset();
    }
  };

  const performFullReset = async () => {
    // Clear all step results from IndexedDB
    await resetProjectAnalysis();
    setCompletedSteps([]);
    setActiveStep(null);
    setHasStartedAnalysis(true);
    setIsProcessing(false);
    setAnalysisError("");
    setBackendResult(null);
  };

  const handleResetWarningConfirm = async () => {
    await performFullReset();
    setShowResetWarningModal(false);
  };

  const handleResetWarningCancel = () => {
    setShowResetWarningModal(false);
  };

  const handleResetStep = async () => {
    if (activeStep) {
      await deleteStepResult(activeStep);
      setCompletedSteps((prev) => prev.filter((step) => step !== activeStep));
      setIsProcessing(false);
      setAnalysisError("");
    }
  };

  if (!selectedProject) {
    return (
      <Paper
        elevation={1}
        className={`flex-1 p-8 flex flex-col items-center justify-center text-center ${className}`}
        sx={{
          backgroundColor: "#fafafa",
          borderRadius: "12px",
          border: "1px solid #e0e0e0",
          height: "100%",
          minHeight: "100%",
          overflow: "auto",
        }}
      >
        <Typography variant="h4" className="text-gray-500 mb-4">
          No project selected
        </Typography>
        <Typography variant="body1" className="text-gray-400">
          Please select a project from the sidebar to view its details and
          analysis
        </Typography>
      </Paper>
    );
  }

  return (
    <div className={`flex-1 flex flex-col h-full overflow-hidden ${className}`}>
      <div className="p-6 bg-white border-b border-gray-200">
        <div className="flex items-start justify-between mb-4">
          <div>
            <Typography
              variant="h3"
              className="text-gray-900 mb-2"
              sx={{ fontWeight: 700, fontSize: "2rem" }}
            >
              {selectedProject.project_name}
            </Typography>
            <Typography
              variant="body1"
              className="text-gray-600 mb-2"
              sx={{ fontSize: "1.1rem" }}
            >
              Description: {selectedProject.project_description}
            </Typography>
            <div className="flex items-center gap-4">
              <Chip
                label={`Type: ${selectedProject.data_type}`}
                variant="outlined"
                size="small"
                sx={{
                  borderColor: "#19C1DB",
                  color: "#19C1DB",
                  fontWeight: 500,
                }}
              />
              <Typography variant="body2" className="text-gray-500">
                Created at: {new Date().toLocaleDateString()}
              </Typography>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outlined"
              startIcon={<AutoAwesomeIcon />}
              onClick={() => setShowDemosModal(true)}
              sx={{ textTransform: "none" }}
            >
              Demo results
            </Button>
          <ProjectEditor
            onViewFiles={handleViewFiles}
            onInfo={handleInfo}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onReset={handleReset}
            buttonText={fileCount === 0 ? "Add Files" : "View Files"}
            showReset={completedSteps.length > 0}
          />
          </div>
        </div>
      </div>
      <BaseModal
        open={showDemosModal}
        onClose={() => setShowDemosModal(false)}
        title="Precomputed demo results"
        maxWidth="md"
      >
        <PrecomputedDemosPanel projectName={selectedProject.project_name} onOpen={handleResumeRun} />
      </BaseModal>

      {/* Main Content Area */}
      <div className="flex-1 flex overflow-hidden">
        {fileCount > 0 && (
          <ResizablePanel
            title="Analysis"
            position="left"
            minWidth={150}
            maxWidth={600}
            defaultWidth={420}
            storageKey="analysisPanel"
          >
            <WorkflowPanel
              fileCount={fileCount}
              selectedFiles={selectedFiles}
              uploadedFileNames={uploadedFileNames}
              onFileSelect={handleFileSelect}
              onSelectAllFiles={handleSelectAllFiles}
              onRunSelectedFiles={handleRunSelectedFiles}
              hasStartedAnalysis={hasStartedAnalysis}
              onStepSelect={handleStepSelect}
              completedSteps={completedSteps}
            />
          </ResizablePanel>
        )}

        {/* Right Panel - Main Content */}
        <div className="flex-1 p-4 h-full min-w-0 overflow-hidden">
          {activeStep ? (
            <StepDetailsPanel
              key={activeStep}
              stepId={activeStep}
              stepTitle={
                activeStep
                  ? `Step ${currentStepConfig.stepOrder.indexOf(activeStep) + 1}: ${currentStepConfig.steps.find((step) => step.id === activeStep)?.title || currentStepConfig.stepNameMapping[activeStep] || activeStep}`
                  : "Step Details"
              }
              stepDescription=""
              isActive={true}
              isProcessing={isProcessing}
              results={analysisError ? undefined : stepResults[activeStep]}
              error={analysisError}
              onClose={handleStepClose}
              onSubmit={handleStepSubmit}
              onBack={stepResults[activeStep] ? handleBack : handleStepBack}
              onContinue={handleContinue}
              onCancelProcessing={handleCancelProcessing}
              onResetStep={handleResetStep}
              onResampleQc={handleResampleQc}
              projectContext={selectedProject.project_name + " " + (selectedProject.project_description || "")}
              annotationResult={stepResults["cell-type-annotation"]}
            />
          ) : fileCount === 0 ? (
            <Paper
              elevation={1}
              className="flex flex-col items-center justify-center text-center h-full"
              sx={{
                backgroundColor: "#fafafa",
                borderRadius: "12px",
                border: "1px solid #e0e0e0",
                minHeight: "400px",
              }}
            >
              <CloudUploadIcon sx={{ fontSize: 80, color: "#19C1DB", mb: 3 }} />
              <Typography
                variant="h4"
                className="text-gray-900 mb-4"
                sx={{ fontWeight: 600 }}
              >
                Great! Now you just have to add the data for the project
              </Typography>
              <Typography
                variant="body1"
                className="text-gray-600 mb-8 max-w-md"
                sx={{ fontSize: "1.1rem", lineHeight: 1.6 }}
              >
                Upload your data files to get started with the analysis. You can
                upload single-cell RNA sequencing data, bulk RNA-seq, or other
                multi-omics datasets.
              </Typography>

              <div className="flex gap-4">
                <Button
                  variant="contained"
                  startIcon={<CloudUploadIcon />}
                  onClick={handleViewFiles}
                  className="bg-[#19C1DB] hover:bg-[#17B1CB] text-white font-semibold py-3 px-8"
                  sx={{
                    textTransform: "none",
                    fontSize: "1.1rem",
                    borderRadius: "8px",
                  }}
                >
                  Upload Data
                </Button>

                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  className="border-[#19C1DB] text-[#19C1DB] hover:bg-[#19C1DB] hover:text-white font-semibold py-3 px-8"
                  sx={{
                    textTransform: "none",
                    fontSize: "1.1rem",
                    borderRadius: "8px",
                  }}
                >
                  Download Sample
                </Button>
              </div>
            </Paper>
          ) : selectedFiles.length === 0 ? (
            <Paper
              elevation={1}
              className="flex flex-col items-center justify-center text-center h-full"
              sx={{
                backgroundColor: "#fafafa",
                borderRadius: "12px",
                border: "1px solid #e0e0e0",
                minHeight: "400px",
              }}
            >
              <Typography variant="h4" className="text-gray-500 mb-4">
                Select Files to Begin Analysis
              </Typography>
              <Typography variant="body1" className="text-gray-400">
                Your project has {fileCount} files. Please select files from the
                left panel to begin analysis.
              </Typography>
            </Paper>
          ) : (
            <Paper
              elevation={1}
              className="flex flex-col items-center justify-center text-center h-full"
              sx={{
                backgroundColor: "#fafafa",
                borderRadius: "12px",
                border: "1px solid #e0e0e0",
                minHeight: "400px",
              }}
            >
              <Typography variant="h4" className="text-gray-500 mb-4">
                Analysis Ready
              </Typography>
              <Typography variant="body1" className="text-gray-400 mb-6">
                {selectedFiles.length} file(s) selected. Start with Quality
                Control from the left panel to begin your analysis.
              </Typography>

              <div className="w-full max-w-md">
                <div className="flex items-center justify-between mb-3">
                  <Typography
                    variant="h6"
                    className="text-gray-600"
                    sx={{ fontWeight: 600 }}
                  >
                    Selected Files:
                  </Typography>
                  {selectedFiles.length > 0 && (
                    <Button
                      variant="text"
                      size="small"
                      onClick={() => setSelectedFiles([])}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      sx={{ textTransform: "none" }}
                    >
                      Clear All
                    </Button>
                  )}
                </div>
                <div className="space-y-2 text-left">
                  {selectedFiles.map((fileName, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-200"
                    >
                      <div className="w-2 h-2 bg-[#19C1DB] rounded-full flex-shrink-0" />
                      <Typography
                        variant="body2"
                        className="text-gray-700 flex-1"
                        sx={{ fontWeight: 500 }}
                      >
                        {fileName}
                      </Typography>
                      <IconButton
                        onClick={() => handleFileSelect(fileName, false)}
                        size="small"
                        sx={{
                          color: "#ef4444",
                          "&:hover": {
                            backgroundColor: "#fef2f2",
                            color: "#dc2626",
                          },
                        }}
                        title="Remove file from selection"
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </div>
                  ))}
                </div>
              </div>
            </Paper>
          )}
        </div>
      </div>

      <BaseModal
        open={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        title="Upload Files"
        maxWidth="lg"
      >
        <FileUploadForm
          onSubmit={handleFileUploadSubmit}
          onCancel={() => setShowUploadModal(false)}
          selectedFiles={selectedFiles}
          onRunSelectedFiles={handleRunSelectedFilesFromModal}
          onFilesChanged={handleFilesChanged}
          files={projectFiles}
        />
      </BaseModal>

      <BaseModal
        open={showInfoModal}
        onClose={() => setShowInfoModal(false)}
        title="Workflow Instructions"
        maxWidth="md"
      >
        <WorkflowInfo />
      </BaseModal>

      <BaseModal
        open={showEditModal}
        onClose={() => setShowEditModal(false)}
        title="Edit Project"
        maxWidth="md"
      >
        {selectedProject && (
          <ProjectForm
            mode="edit"
            project={selectedProject}
            onSubmit={handleEditSubmit}
            onCancel={() => setShowEditModal(false)}
          />
        )}
      </BaseModal>

      <BaseModal
        open={showDeleteModal}
        onClose={handleDeleteCancel}
        title="Delete Project"
        maxWidth="sm"
      >
        <ConfirmationForm
          onConfirm={handleDeleteConfirm}
          onCancel={handleDeleteCancel}
          confirmText="Yes, Delete"
          cancelText="Cancel"
          loading={isDeleting}
        />
      </BaseModal>

      <ResetWarningModal
        open={showBackWarningModal}
        onClose={handleBackWarningCancel}
        onConfirm={handleBackWarningConfirm}
        currentStep={activeStep || ""}
        targetStep={targetStepForBack || ""}
        stepsToReset={
          targetStepForBack
            ? (() => {
                const targetStepIndex =
                  currentStepConfig.stepOrder.indexOf(targetStepForBack);
                const currentStepIndex = currentStepConfig.stepOrder.indexOf(
                  activeStep || "",
                );

                if (targetStepForBack === activeStep) {
                  return currentStepConfig.stepOrder
                    .slice(currentStepIndex + 1)
                    .filter((step) => completedSteps.includes(step));
                } else {
                  return currentStepConfig.stepOrder
                    .slice(targetStepIndex + 1)
                    .filter((step) => completedSteps.includes(step));
                }
              })()
            : []
        }
      />

      <ResetWarningModal
        open={showResetWarningModal}
        onClose={handleResetWarningCancel}
        onConfirm={handleResetWarningConfirm}
        currentStep="all"
        targetStep="quality-control"
        stepsToReset={(() => {
          // For "Reset All Analysis Progress", show all completed steps except quality-control
          return completedSteps.filter((step) => step !== "quality-control");
        })()}
        title="Reset All Analysis Progress"
        message="Accepting will reset all analysis progress back to Step 1: Quality Control. All following steps will be cleared and need to be run again."
        confirmButtonText="Reset to Step 1"
      />

      <PostClusteringModal
        open={showPostClusteringModal}
        onClose={() => setShowPostClusteringModal(false)}
        onStepSelect={setActiveStep}
      />
    </div>
  );
};

export default ProjectPage;
